import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Module-level singleton that tracks which onboarding tooltip is currently
 * visible. Only one tooltip may be active at a time.
 *
 * Priority rules:
 *   - Higher priority always wins over lower priority.
 *   - Equal priority: last caller wins (same behaviour as before).
 *   - When the active tooltip is hidden, the slot is cleared — lower-priority
 *     tooltips do NOT automatically re-surface (caller must re-invoke show()).
 */

type Listener = (activeId: string | null) => void;

interface Slot {
  id: string;
  priority: number;
}

let current: Slot | null = null;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((l) => l(current?.id ?? null));
}

function requestActive(id: string, priority: number): boolean {
  if (current && current.priority > priority) {
    // A higher-priority tooltip is already showing — do not steal the slot.
    return false;
  }
  current = { id, priority };
  notify();
  return true;
}

function releaseActive(id: string) {
  if (current?.id !== id) return;
  current = null;
  notify();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// ---------------------------------------------------------------------------

/** @internal Reset singleton state — for unit tests only. */
export function __resetForTesting() {
  current = null;
  listeners.clear();
}

/**
 * Hook that gives a component a named slot in the global tooltip queue.
 *
 * @param id        Stable string that uniquely identifies this tooltip site.
 * @param priority  Higher number = higher priority (default: 0).
 *                  A higher-priority show() always preempts a lower-priority
 *                  active tooltip. Equal-priority: last caller wins.
 *
 * @returns
 *   - `isActive`  — whether this tooltip should be visible right now.
 *   - `show()`    — claim the active slot (respects priority).
 *   - `hide()`    — release the active slot.
 *
 * @example
 * ```tsx
 * // JoinRequest — high priority
 * const { isActive, show, hide } = useActiveTooltip('join-request-assign-task', 10);
 *
 * // ChatForm — default priority
 * const { isActive, show, hide } = useActiveTooltip('chat-form-tooltip');
 *
 * useEffect(() => { if (shouldShow) show(); else hide(); }, [shouldShow]);
 * <TooltipOnboardingWeb open={isActive} forceShow={isActive} ... />
 * ```
 */
export function useActiveTooltip(id: string, priority = 0) {
  const idRef = useRef(id);
  idRef.current = id;
  const priorityRef = useRef(priority);
  priorityRef.current = priority;

  const [isActive, setIsActive] = useState(() => current?.id === id);

  useEffect(() => {
    setIsActive(current?.id === idRef.current);

    const unsubscribe = subscribe((activeId) => {
      setIsActive(activeId === idRef.current);
    });

    return () => {
      unsubscribe();
      // Release slot on unmount if we own it.
      releaseActive(idRef.current);
    };
  }, []);

  const show = useCallback(() => {
    requestActive(idRef.current, priorityRef.current);
  }, []);

  const hide = useCallback(() => {
    releaseActive(idRef.current);
  }, []);

  return { isActive, show, hide } as const;
}
